import { supabase } from "@/integrations/supabase/client";
import {
  PLATFORM_SHARE_PERCENTAGE,
  AUTHOR_SHARE_PERCENTAGE,
} from "./subscriptionService";

export interface AuthorEarnings {
  authorId: string;
  fromSubscriptions: number;
  fromPlatformReads: number;
  fromDirectPurchases: number;
  fromTips: number;
  total: number;
  paidOut: number;
}

type EarningsField = "from_subscriptions" | "from_platform_reads" | "from_direct_purchases" | "from_tips";

const addToEarnings = async (
  authorId: string,
  field: EarningsField,
  amount: number
): Promise<void> => {
  const { error } = await supabase.rpc("add_to_author_earnings", {
    p_author_id: authorId,
    p_field:     field,
    p_amount:    amount,
  });
  if (error) console.error("Error updating author earnings:", error);
};

// Calls the record-premium-read Edge Function which:
//   1. Verifies subscription from DB (not localStorage)
//   2. Calculates earnings proportional to episode word count
//   3. Deduplicates via premium_read_events table (persistent across sessions)
export const recordPremiumRead = async (episodeId: string): Promise<void> => {
  const { error } = await supabase.functions.invoke("record-premium-read", {
    body: { episode_id: episodeId },
  });
  if (error) console.error("Error recording premium read:", error);
};

export const updateAuthorEarningsFromSubscription = async (
  authorId: string,
  amount: number
): Promise<void> => {
  const authorShare = parseFloat((amount * (AUTHOR_SHARE_PERCENTAGE / 100)).toFixed(2));
  await addToEarnings(authorId, "from_subscriptions", authorShare);
};

export const updateAuthorEarningsFromPurchase = async (
  authorId: string,
  amount: number
): Promise<void> => {
  const authorShare = parseFloat((amount * (AUTHOR_SHARE_PERCENTAGE / 100)).toFixed(2));
  await addToEarnings(authorId, "from_direct_purchases", authorShare);
};

export const updateAuthorEarningsFromTip = async (
  authorId: string,
  amount: number
): Promise<void> => {
  const authorShare = parseFloat((amount * 0.9).toFixed(2));
  await addToEarnings(authorId, "from_tips", authorShare);
};

export const getAuthorEarnings = async (authorId: string): Promise<AuthorEarnings> => {
  const { data, error } = await supabase
    .from("author_earnings")
    .select("from_subscriptions, from_platform_reads, from_direct_purchases, from_tips, total, paid_out")
    .eq("author_id", authorId)
    .maybeSingle();

  if (error) console.error("Error fetching author earnings:", error);

  return {
    authorId,
    fromSubscriptions:    Number(data?.from_subscriptions    ?? 0),
    fromPlatformReads:    Number(data?.from_platform_reads   ?? 0),
    fromDirectPurchases:  Number(data?.from_direct_purchases ?? 0),
    fromTips:             Number(data?.from_tips             ?? 0),
    total:                Number(data?.total                 ?? 0),
    paidOut:              Number(data?.paid_out              ?? 0),
  };
};

export const getMonthlyEarnings = async (
  authorId: string,
  year: number,
  month: number
): Promise<number> => {
  const { data, error } = await supabase
    .from("author_earnings_monthly")
    .select("from_subscriptions, from_platform_reads, from_direct_purchases, from_tips")
    .eq("author_id", authorId)
    .eq("year", year)
    .eq("month", month)
    .maybeSingle();

  if (error) console.error("Error fetching monthly earnings:", error);
  if (!data) return 0;

  return Number(data.from_subscriptions) + Number(data.from_platform_reads) +
         Number(data.from_direct_purchases) + Number(data.from_tips);
};

export const getMonthReadUnits = async (
  authorId: string,
  year: number,
  month: number
): Promise<number> => {
  const monthStart = new Date(Date.UTC(year, month - 1, 1)).toISOString();
  const monthEnd   = new Date(Date.UTC(year, month, 1)).toISOString();

  const { data, error } = await supabase
    .from("premium_read_events")
    .select("word_count")
    .eq("author_id", authorId)
    .gte("created_at", monthStart)
    .lt("created_at", monthEnd);

  if (error) console.error("Error fetching month read units:", error);
  return (data ?? []).reduce((sum, row) => sum + (row.word_count ?? 0), 0);
};

export const getPlatformRevenue = async (): Promise<number> => {
  const { data, error } = await supabase
    .from("author_earnings")
    .select("total");

  if (error) {
    console.error("Error fetching platform revenue:", error);
    return 0;
  }

  const totalAuthorRevenue = (data ?? []).reduce((sum, row) => sum + Number(row.total), 0);
  const totalRevenue = totalAuthorRevenue / (AUTHOR_SHARE_PERCENTAGE / 100);
  return parseFloat((totalRevenue * (PLATFORM_SHARE_PERCENTAGE / 100)).toFixed(2));
};
